/**
 * useMerchantDashboard Hook - Aggregator Pattern
 *
 * Aggregates merchant dashboard data from multiple sources.
 */

import { useGetDashboard } from '@/generated/hooks';
import type { AggregatorPattern } from '../../types/hook-patterns.types';
import type { MerchantDashboardData, MerchantDashboardComputed } from '@/types/controller.types';

/**
 * Aggregates merchant dashboard data
 */
export function useMerchantDashboard(
	selectedMerchantId: number | null = null,
): AggregatorPattern<MerchantDashboardData, MerchantDashboardComputed> {
	const hasSelectedMerchantId = selectedMerchantId !== null && selectedMerchantId > 0;
	const merchantIdForQuery = selectedMerchantId ?? 0;

	const dashboardQuery = useGetDashboard(
		{ merchantId: merchantIdForQuery },
		{ enabled: hasSelectedMerchantId },
	);

	const dashboardData = dashboardQuery.data;
	const activeCouponCount = dashboardData?.active_coupon_count ?? 0;
	const todayRedemptions = dashboardData?.today_redemptions ?? 0;
	const memberCount = dashboardData?.member_count ?? 0;

	const data: MerchantDashboardData = {
		merchants: null,
		selectedMerchant: null,
		dashboard: dashboardData ?? null,
		stats: dashboardData ?? null,
		recentActivity: null,
	};

	// Simple computed values
	const computed: MerchantDashboardComputed = {
		hasMerchants: false,
		merchantCount: 0,
		hasDashboardData: dashboardData !== null && dashboardData !== undefined,
		totalCoupons: activeCouponCount,
		activeCoupons: activeCouponCount,
		totalRedemptions: todayRedemptions,
		totalMembers: memberCount,
		redemptionRate:
			activeCouponCount > 0
				? Math.round((todayRedemptions / activeCouponCount) * 100)
				: 0,
		isValidSelection: hasSelectedMerchantId,
		selectedMerchantName: null,
		selectedMerchantType: null,
	};

	return {
		data,
		computed,
		isLoading: dashboardQuery.isLoading,
		error: dashboardQuery.error?.message ?? null,
	};
}

export default useMerchantDashboard;
